import { useState, useCallback, useRef, useEffect } from "react";
import { T, Badge, Card, Btn, DataTable, StatCard, fmtCcy, fmtDate, fmtDateTime, font, mono, ST, Inp, Sel } from '../theme.js';
import { ContactType, TaskStatus, ComplaintChannel, ComplaintStatus } from '../engines/crm-engine.js';
import { KYCStatus } from '../engines/kyc-engine.js';
import { DataCollectionPanel, ScreeningPanel, RMReviewPanel, ApprovedPanel, ReviewDuePanel, RejectedPanel, FSCSTab } from './KYCModule.jsx';
import { api } from '../api-client.js';

const CRM_NAV = [
  { id: 'portfolio', label: 'My Portfolio' },
  { id: 'customer360', label: 'Customer 360' },
  { id: 'tasks', label: 'My Tasks' },
  { id: 'complaints', label: 'Complaints' },
  { id: 'pipelines', label: 'Pipelines' },
  { id: 'contact', label: 'Add Contact' },
];

const riskColor = r => ({ HIGH: 'red', MEDIUM: 'amber', LOW: 'green', PROHIBITED: 'red' }[r] || 'gray');
const priorityColor = p => ({ HIGH: 'red', MEDIUM: 'amber', LOW: 'gray' }[p] || 'gray');
const kycStatusColor = s => ({ APPROVED: 'green', REJECTED: 'red', RM_REVIEW: 'amber', SCREENING: 'blue', DATA_COLLECTION: 'gray', REVIEW_DUE: 'red', UNDER_REVIEW: 'amber', CLOSED: 'gray' }[s] || 'gray');

// ============================================================
// CUSTOMER TYPEAHEAD
// ============================================================

function CustomerTypeahead({ customers, value, onSelect, label = 'Customer' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedName, setSelectedName] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    if (value && !selectedName) {
      const c = customers.find(c => c.id === value);
      if (c) setSelectedName(c.legalName);
    }
  }, [value, customers, selectedName]);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const sorted = [...customers].sort((a, b) => (a.legalName || '').localeCompare(b.legalName || ''));
  const filtered = query.length > 0
    ? sorted.filter(c => c.legalName.toLowerCase().includes(query.toLowerCase())).slice(0, 15)
    : sorted.slice(0, 15);
  const totalMatches = query.length > 0
    ? sorted.filter(c => c.legalName.toLowerCase().includes(query.toLowerCase())).length
    : sorted.length;

  const handleSelect = (c) => {
    setSelectedName(c.legalName);
    setQuery('');
    setOpen(false);
    onSelect(c.id);
  };

  const handleClear = () => {
    setSelectedName('');
    setQuery('');
    onSelect('');
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', marginBottom: 12 }}>
      {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: T.textSecondary, marginBottom: 4 }}>{label}</label>}
      {selectedName ? (
        <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, fontSize: 13, fontFamily: font }}>
          <span style={{ flex: 1, color: T.text }}>{selectedName}</span>
          <button onClick={handleClear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, fontSize: 15, padding: '0 4px', lineHeight: 1 }}>&times;</button>
        </div>
      ) : (
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search customers..."
          style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: font, color: T.text, background: T.surface, boxSizing: 'border-box', outline: 'none' }}
        />
      )}
      {open && !selectedName && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, marginTop: 2, maxHeight: 450, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '12px', fontSize: 12, color: T.textMuted, textAlign: 'center' }}>No customers found</div>
          ) : (
            <>
              <div style={{ padding: '6px 12px', fontSize: 11, color: T.textMuted, borderBottom: `1px solid ${T.borderLight}` }}>
                {query.length > 0 ? `${totalMatches} customer${totalMatches !== 1 ? 's' : ''} found` : `${totalMatches} customers`}
              </div>
              {filtered.map(c => (
                <div key={c.id} onClick={() => handleSelect(c)}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, borderBottom: `1px solid ${T.borderLight}` }}
                  onMouseEnter={e => e.currentTarget.style.background = T.blueBg}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ fontWeight: 500, color: T.text }}>{c.legalName}</div>
                  {c.registrationNumber && <div style={{ fontSize: 11, color: T.textMuted }}>{c.registrationNumber}</div>}
                </div>
              ))}
              {totalMatches > 15 && (
                <div style={{ padding: '6px 12px', fontSize: 11, color: T.textMuted, textAlign: 'center' }}>Type to narrow {totalMatches - 15} more...</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN MODULE
// ============================================================

const NAV_LABELS = { portfolio: 'Portfolio', customer360: 'Customer 360', tasks: 'Tasks', complaints: 'Complaints', pipelines: 'Pipelines', contact: 'Add Contact' };

export default function CRMModule({ crm, kyc, ledger, lending, monitoring, compliance, refresh: parentRefresh, runQualityGate, extractActivityProfile, initialContext }) {
  const [nav, setNav] = useState(() => {
    if (initialContext?.entityType === 'customer360' && initialContext?.customerId) {
      return { view: 'customer360', customerId: initialContext.customerId, returnTo: 'portfolio' };
    }
    if (initialContext?.entityType === 'complaint') {
      return { view: 'complaints', customerId: null, returnTo: 'portfolio' };
    }
    if (initialContext?.entityType === 'task') {
      return { view: 'tasks', customerId: null, returnTo: 'portfolio' };
    }
    return { view: 'portfolio', customerId: null, returnTo: null };
  });
  const [tick, setTick] = useState(0);
  const [toast, setToast] = useState(null);
  const toastRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    api.get('/me').then(user => setCurrentUser(user)).catch(() => {});
  }, []);

  // React to initialContext changes without requiring a full remount
  useEffect(() => {
    if (!initialContext) return;
    if (initialContext.entityType === 'customer360' && initialContext.customerId) {
      setNav({ view: 'customer360', customerId: initialContext.customerId, returnTo: 'portfolio' });
    } else if (initialContext.entityType === 'complaint') {
      setNav({ view: 'complaints', customerId: null, returnTo: 'portfolio' });
    } else if (initialContext.entityType === 'task') {
      setNav({ view: 'tasks', customerId: null, returnTo: 'portfolio' });
    }
  }, [initialContext]);

  const rmId = currentUser?.id;

  const refresh = useCallback(async () => {
    setTick(t => t + 1);
    if (parentRefresh) await parentRefresh();
  }, [parentRefresh]);

  const showToast = useCallback((msg, type = 'success') => {
    if (toastRef.current) clearTimeout(toastRef.current);
    setToast({ msg, type });
    toastRef.current = setTimeout(() => setToast(null), 3500);
  }, []);
  const showError = useCallback(msg => showToast(msg, 'error'), [showToast]);

  const openCustomer = (custId, returnView) => setNav({ view: 'customer360', customerId: custId, returnTo: returnView || nav.view });

  const goBack = useCallback(() => {
    setNav(prev => ({ view: prev.returnTo || 'portfolio', customerId: null, returnTo: null }));
  }, []);

  if (!currentUser) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: T.textMuted }}>Loading user profile...</div>
      </div>
    );
  }

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', top: 12, right: 12, zIndex: 1000, padding: '10px 16px',
          background: toast.type === 'error' ? T.redBg : T.greenBg,
          border: `1px solid ${toast.type === 'error' ? T.red : T.green}40`,
          borderRadius: 6, fontSize: 12, maxWidth: 420, fontFamily: font }}>
          <span style={{ color: toast.type === 'error' ? T.red : T.green, marginRight: 8 }}>{toast.type === 'error' ? '\u2717' : '\u2713'}</span>{toast.msg}
        </div>
      )}

      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: `1px solid ${T.border}`, overflowX: 'auto' }}>
        {CRM_NAV.map(n => {
          const activeTab = nav.returnTo || nav.view;
          const isActive = activeTab === n.id;
          return (
            <button key={n.id} onClick={() => setNav({ view: n.id, customerId: n.id === 'customer360' ? nav.customerId : null, returnTo: null })} style={{
              padding: '8px 14px', fontSize: 12,
              fontWeight: isActive ? 600 : 400,
              color: isActive ? T.blue : T.textSecondary,
              background: 'transparent', border: 'none',
              borderBottom: isActive ? `2px solid ${T.blue}` : '2px solid transparent',
              cursor: 'pointer', fontFamily: font, marginBottom: -1, whiteSpace: 'nowrap',
            }}>
              {n.label}
            </button>
          );
        })}
      </div>

      {nav.view === 'portfolio' && <PortfolioView crm={crm} kyc={kyc} ledger={ledger} lending={lending} monitoring={monitoring} rmId={rmId} openCustomer={openCustomer} key={tick} />}
      {nav.view === 'customer360' && <Customer360View crm={crm} kyc={kyc} monitoring={monitoring} customerId={nav.customerId} openCustomer={openCustomer} onBack={goBack} backLabel={NAV_LABELS[nav.returnTo] || 'Portfolio'} refresh={refresh} showToast={showToast} showError={showError} runQualityGate={runQualityGate} extractActivityProfile={extractActivityProfile} currentUser={currentUser} key={nav.customerId || 'no-cust'} />}
      {nav.view === 'tasks' && <TasksView crm={crm} kyc={kyc} rmId={rmId} openCustomer={openCustomer} refresh={refresh} showToast={showToast} showError={showError} key={tick} />}
      {nav.view === 'complaints' && <ComplaintsView crm={crm} kyc={kyc} openCustomer={openCustomer} refresh={refresh} showToast={showToast} showError={showError} initialEntityId={initialContext?.entityType === 'complaint' ? initialContext?.entityId : null} key={tick} />}
      {nav.view === 'pipelines' && <PipelinesView crm={crm} openCustomer={openCustomer} key={tick} />}
      {nav.view === 'contact' && <AddContactView crm={crm} kyc={kyc} rmId={rmId} refresh={refresh} showToast={showToast} showError={showError} key={tick} />}
    </div>
  );
}


// ============================================================
// MY PORTFOLIO
// ============================================================

function PortfolioView({ crm, kyc, ledger, lending, monitoring, rmId, openCustomer }) {
  const [portfolio, setPortfolio] = useState(crm.getRMPortfolio(rmId));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (crm.loadPortfolio) {
      setLoading(true);
      crm.loadPortfolio(rmId)
        .then(data => { setPortfolio(data || []); setLoading(false); })
        .catch(() => setLoading(false));
    }
  }, [crm, rmId]);

  const sorted = [...portfolio].sort((a, b) => (a.legalName || '').localeCompare(b.legalName || ''));

  const overdueTasks = crm.getOverdueTasks().filter(t => t.assignedTo === rmId);
  const rmTasks = crm.getTasksByRM(rmId);

  // KYC status breakdown
  const kycCounts = {};
  for (const c of portfolio) {
    kycCounts[c.kycStatus] = (kycCounts[c.kycStatus] || 0) + 1;
  }

  // Lending exposure: sum drawnAmount of active facilities for portfolio customer IDs
  const portfolioIds = new Set(portfolio.map(c => c.customerId));
  const allFacilities = lending ? lending.getAllFacilities() : [];
  const portfolioFacilities = allFacilities.filter(f => portfolioIds.has(f.customerId) && f.status === 'ACTIVE');
  const totalExposure = portfolioFacilities.reduce((s, f) => s + (f.drawnAmount || 0), 0);

  // Customers requiring attention
  const attentionStatuses = new Set(['REVIEW_DUE', 'SCREENING', 'RM_REVIEW']);
  const attentionCustomers = portfolio.filter(c =>
    attentionStatuses.has(c.kycStatus) || c.openAlertCount > 0
  );

  // Upcoming tasks: next 5 by dueDate ascending
  const upcomingTasks = [...rmTasks]
    .filter(t => t.dueDate)
    .sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''))
    .slice(0, 5);

  // Recent contacts: from crm.contacts Map, filter to portfolio customers, sort by date desc, take 10
  const recentContacts = Array.from(crm.contacts ? crm.contacts.values() : [])
    .filter(c => portfolioIds.has(c.customerId))
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    .slice(0, 10);

  // Build a customer name lookup from portfolio data
  const custName = {};
  for (const c of portfolio) custName[c.customerId] = c.legalName;

  if (loading && portfolio.length === 0) {
    return <div><ST>My Portfolio</ST><Card><div style={{ color: T.textMuted, padding: 20, textAlign: 'center' }}>Loading portfolio...</div></Card></div>;
  }

  return (
    <div>
      <ST>My Portfolio</ST>

      {/* Summary cards row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Total Customers" value={portfolio.length} />
        <Card style={{ flex: 1, minWidth: 180, padding: 14 }}>
          <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 8 }}>KYC Status</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[['APPROVED', 'green'], ['SCREENING', 'blue'], ['RM_REVIEW', 'amber'], ['REVIEW_DUE', 'red']].map(([st, col]) => (
              <span key={st} style={{ fontSize: 12, fontFamily: font }}>
                <Badge color={col}>{st.replace('_', ' ')}</Badge>
                <span style={{ marginLeft: 4, fontWeight: 600, color: T.text }}>{kycCounts[st] || 0}</span>
              </span>
            ))}
          </div>
        </Card>
        <StatCard label="Lending Exposure" value={fmtCcy(totalExposure)} color={T.purple} />
        <StatCard label="Open Alerts" value={portfolio.reduce((s, c) => s + c.openAlertCount, 0)} color={T.red} />
        <StatCard label="Overdue Tasks" value={overdueTasks.length} color={overdueTasks.length > 0 ? T.red : T.textSecondary} />
      </div>

      {/* Two-column dashboard layout */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, alignItems: 'flex-start' }}>

        {/* Left column: Customers Requiring Attention */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Card style={{ padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12, fontFamily: font }}>Customers Requiring Attention</div>
            {attentionCustomers.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: T.textMuted, fontSize: 12 }}>All customers in good standing</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {attentionCustomers.slice(0, 15).map(c => (
                  <div key={c.customerId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, background: T.surfaceAlt, fontSize: 12, fontFamily: font }}>
                    <span
                      style={{ cursor: 'pointer', color: T.blue, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      onClick={() => openCustomer(c.customerId, 'portfolio')}
                    >{c.legalName}</span>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      {attentionStatuses.has(c.kycStatus) && <Badge color={kycStatusColor(c.kycStatus)}>{c.kycStatus.replace('_', ' ')}</Badge>}
                      {c.openAlertCount > 0 && <Badge color="red">{c.openAlertCount} alert{c.openAlertCount > 1 ? 's' : ''}</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Right column: Upcoming Tasks + Recent Activity */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card style={{ padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12, fontFamily: font }}>Upcoming Tasks</div>
            {upcomingTasks.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: T.textMuted, fontSize: 12 }}>No upcoming tasks</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {upcomingTasks.map(t => {
                  const isOverdue = t.dueDate && t.dueDate < new Date().toISOString();
                  return (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, background: T.surfaceAlt, fontSize: 12, fontFamily: font, cursor: t.customerId ? 'pointer' : 'default' }} onClick={() => t.customerId && openCustomer(t.customerId, 'portfolio')}>
                      <span style={{ flex: 1, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
                      {t.customerId && custName[t.customerId] && <span style={{ color: T.textMuted, fontSize: 11, flexShrink: 0 }}>{custName[t.customerId]}</span>}
                      <span style={{ color: isOverdue ? T.red : T.textSecondary, fontSize: 11, fontWeight: isOverdue ? 600 : 400, flexShrink: 0 }}>{fmtDate(t.dueDate)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card style={{ padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12, fontFamily: font }}>Recent Activity</div>
            {recentContacts.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: T.textMuted, fontSize: 12 }}>No recent contacts recorded</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {recentContacts.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, background: T.surfaceAlt, fontSize: 12, fontFamily: font, cursor: 'pointer' }} onClick={() => openCustomer(c.customerId, 'portfolio')}>
                    <Badge color="gray">{(c.type || '').replace('_', ' ')}</Badge>
                    <span style={{ flex: 1, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subject}</span>
                    {custName[c.customerId] && <span style={{ color: T.textMuted, fontSize: 11, flexShrink: 0 }}>{custName[c.customerId]}</span>}
                    <span style={{ color: T.textSecondary, fontSize: 11, flexShrink: 0 }}>{fmtDateTime(c.date)}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Full portfolio table */}
      <Card style={{ padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12, fontFamily: font }}>Full Portfolio</div>
        {sorted.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>No customers assigned to this RM</div>
        ) : (
          <DataTable
            headers={['Customer', 'Risk', 'KYC Status', 'Balance', 'Alerts', 'Tasks', 'Last Contact']}
            rows={sorted.map(c => [
              <span style={{ cursor: 'pointer', color: T.blue, fontWeight: 500 }} onClick={() => openCustomer(c.customerId, 'portfolio')}>{c.legalName}</span>,
              <Badge color={riskColor(c.riskRating)}>{c.riskRating || '\u2014'}</Badge>,
              <Badge color={kycStatusColor(c.kycStatus)}>{c.kycStatus}</Badge>,
              fmtCcy(c.totalBalance),
              c.openAlertCount > 0 ? <span style={{ color: T.red, fontWeight: 600 }}>{c.openAlertCount}</span> : '0',
              c.openTaskCount > 0 ? <span style={{ color: T.amber, fontWeight: 600 }}>{c.openTaskCount}</span> : '0',
              c.daysSinceLastContact !== null
                ? <span style={{ color: c.daysSinceLastContact > 30 ? T.red : c.daysSinceLastContact > 14 ? T.amber : T.textSecondary }}>
                    {c.daysSinceLastContact}d ago
                  </span>
                : <span style={{ color: T.textMuted }}>Never</span>,
            ])}
            compact
          />
        )}
      </Card>
    </div>
  );
}


// ============================================================
// CUSTOMER 360
// ============================================================

// ---- Next Action guidance by KYC status ----
const NEXT_ACTION = {
  DATA_COLLECTION: { action: 'Complete customer data, add directors/UBOs, verify address', owner: 'rm', button: 'Complete Data', scrollTo: 'customer-details' },
  SCREENING: { action: 'Run sanctions, PEP and adverse media screening', owner: 'system', button: null, scrollTo: null },
  RM_REVIEW: { action: 'Complete risk assessment and quality gate review', owner: 'rm', button: 'Complete Assessment', scrollTo: 'open-alerts' },
  APPROVED: { action: null, owner: null, button: null, scrollTo: null },
  REVIEW_DUE: { action: 'Periodic review required — schedule and complete KYC refresh', owner: 'rm', button: 'Start Review', scrollTo: 'customer-details' },
  UNDER_REVIEW: { action: 'KYC review in progress — complete updated assessment', owner: 'rm', button: 'Complete Assessment', scrollTo: 'open-alerts' },
  REJECTED: { action: 'Customer rejected — no further action', owner: null, button: null, scrollTo: null },
};

const KYC_STAGES = ['DATA_COLLECTION', 'SCREENING', 'RM_REVIEW', 'APPROVED'];

function KycWorkflowProgress({ status }) {
  const isReviewCycle = status === 'REVIEW_DUE' || status === 'UNDER_REVIEW';
  const isRejected = status === 'REJECTED';
  const stages = isReviewCycle ? ['REVIEW_DUE', 'UNDER_REVIEW', 'APPROVED'] : KYC_STAGES;
  const idx = stages.indexOf(status);
  const activeIdx = isRejected ? -1 : idx;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {stages.map((stage, i) => {
        const done = activeIdx >= 0 && i <= activeIdx;
        const current = i === activeIdx;
        const stageColor = isRejected ? T.red : (done ? (current ? T.blue : T.green) : T.surfaceAlt);
        return (
          <div key={stage} style={{ display: 'flex', alignItems: 'center', flex: i < stages.length - 1 ? 1 : 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 600, fontFamily: font,
                background: isRejected ? T.red : stageColor,
                color: done || isRejected ? '#fff' : T.textMuted,
                border: current ? `2px solid ${T.blue}` : 'none',
              }}>{isRejected ? '\u2717' : (done && !current ? '\u2713' : i + 1)}</div>
              <div style={{ fontSize: 9, marginTop: 4, color: done ? T.text : T.textMuted, whiteSpace: 'nowrap' }}>{stage.replace(/_/g, ' ')}</div>
            </div>
            {i < stages.length - 1 && (
              <div style={{ flex: 1, height: 2, background: activeIdx > i ? T.green : T.border, margin: '0 6px', marginBottom: 16 }} />
            )}
          </div>
        );
      })}
      {isRejected && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginLeft: 12, flexShrink: 0 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, background: T.red, color: '#fff' }}>&times;</div>
          <div style={{ fontSize: 9, marginTop: 4, color: T.red }}>REJECTED</div>
        </div>
      )}
    </div>
  );
}

function Customer360View({ crm, kyc, monitoring, customerId, openCustomer, onBack, backLabel, refresh, showToast, showError, runQualityGate, extractActivityProfile, currentUser }) {
  const customers = kyc?.getAllCustomers() || [];
  const [selCust, setSelCust] = useState(customerId || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [c360Tick, setC360Tick] = useState(0);
  const [suggestionsOpen, setSuggestionsOpen] = useState(true);

  const custId = selCust || customerId;

  const crmRef = useRef(crm);
  crmRef.current = crm;

  useEffect(() => {
    if (!custId) { setData(null); setLoadError(null); return; }
    setLoading(true);
    setLoadError(null);
    const c = crmRef.current;
    // Use enhanced endpoint (includes insights) with fallback to basic 360
    Promise.resolve(c.loadCustomer360Enhanced ? c.loadCustomer360Enhanced(custId) : c.loadCustomer360 ? c.loadCustomer360(custId) : c.getCustomer360(custId))
      .then(result => { setData(result); setLoading(false); })
      .catch(err => { setData(null); setLoadError(err); setLoading(false); });
  }, [custId, c360Tick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh that reloads both CRM 360 data and KYC caches
  const refreshC360 = useCallback(async () => {
    if (refresh) await refresh();
    setC360Tick(t => t + 1);
  }, [refresh]);

  // KYC data from the shared KYC cache (for action panels)
  const kycCustomer = kyc?.getCustomer(custId);
  const kycCase = kyc?.getKYCCase(custId);

  // RM assessment draft state (lifted so it survives re-renders)
  const existingAssessment = kycCase?.rmAssessment;
  const [assessmentDraft, setAssessmentDraft] = useState({
    rmId: existingAssessment?.rmId || currentUser?.username || 'rm_user',
    businessUnderstanding: existingAssessment?.businessUnderstanding || '',
    sourceOfWealth: existingAssessment?.sourceOfWealth || '',
    expectedActivity: existingAssessment?.expectedActivity || '',
    concerns: existingAssessment?.concerns || '',
    mitigants: existingAssessment?.mitigants || '',
    recommendation: existingAssessment?.recommendation || 'APPROVE',
    riskRating: existingAssessment?.riskRating || 'LOW',
  });

  if (!custId) {
    return (
      <div>
        <ST>Customer 360</ST>
        <Card>
          <div style={{ marginBottom: 12, fontSize: 12, color: T.textSecondary }}>Search for a customer to view their full profile:</div>
          <CustomerTypeahead customers={customers} value={selCust} onSelect={setSelCust} />
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <ST>Customer 360</ST>
        <Card><div style={{ color: T.textMuted, padding: 20, textAlign: 'center' }}>Loading customer data...</div></Card>
      </div>
    );
  }

  if (loadError) {
    const msg = loadError.status === 403
      ? 'Access denied — you do not have permission to view this customer.'
      : 'Failed to load customer data.';
    return (
      <div>
        <ST>Customer 360</ST>
        <Card><div style={{ color: T.red, padding: 20, textAlign: 'center', fontSize: 13 }}>{msg}</div></Card>
      </div>
    );
  }

  if (!data || !data.customer) {
    return (
      <div>
        <ST>Customer 360</ST>
        <Card><div style={{ color: T.textMuted, padding: 20 }}>Customer not found</div></Card>
      </div>
    );
  }

  const c = data.customer;
  const kycStatus = data.kycStatus || 'DATA_COLLECTION';
  const nextAction = NEXT_ACTION[kycStatus] || NEXT_ACTION.DATA_COLLECTION;
  const rmName = data.assignedRM || data.rmId || 'Assigned RM';
  const insights = data.insights || null;

  const totalBalance = (data.accounts || []).reduce((sum, acc) => {
    const bal = acc.balance ?? acc.posted ?? acc.available ?? 0;
    return sum + (typeof bal === 'number' ? bal : parseFloat(bal) || 0);
  }, 0);
  const activeFacilities = (data.facilities || []).filter(f => f.status === 'ACTIVE');
  const totalExposure = activeFacilities.reduce((sum, f) => sum + (f.committedAmount || f.committed_amount || 0), 0);
  const recentTasks = (data.openTasks || []).slice(0, 3);
  const recentContacts = (data.contactHistory || []).slice(0, 3);

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <span onClick={onBack || (() => { setSelCust(''); setData(null); })}
          style={{ fontSize: 12, color: T.blue, cursor: 'pointer' }}>&larr; Back to {backLabel || 'Portfolio'}</span>
      </div>

      {/* KYC Workflow Progress */}
      <Card style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>KYC WORKFLOW</div>
        <KycWorkflowProgress status={kycStatus} />
      </Card>

      {/* Next Action Card */}
      {kycStatus === 'APPROVED' ? (
        <Card style={{ marginBottom: 16, borderLeft: `4px solid ${T.green}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: T.greenBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: T.green, flexShrink: 0 }}>{'\u2713'}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.green, marginBottom: 2 }}>Active Customer</div>
              <div style={{ fontSize: 12, color: T.textSecondary }}>
                {(data.accounts || []).length} account{(data.accounts || []).length !== 1 ? 's' : ''} — Balance: {fmtCcy(totalBalance)}
                {activeFacilities.length > 0 && <> — {activeFacilities.length} active facilit{activeFacilities.length !== 1 ? 'ies' : 'y'}</>}
              </div>
            </div>
            <Badge color="green">APPROVED</Badge>
          </div>
        </Card>
      ) : (
        <Card style={{ marginBottom: 16, borderLeft: `4px solid ${kycStatus === 'REJECTED' ? T.red : T.amber}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: kycStatus === 'REJECTED' ? T.redBg : (T.amberBg || '#fff8e1'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: kycStatus === 'REJECTED' ? T.red : T.amber, flexShrink: 0 }}>!</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, marginBottom: 2 }}>NEXT ACTION</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 4 }}>{nextAction.action}</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>
                Owner: <span style={{ fontWeight: 500, color: T.textSecondary }}>{nextAction.owner === 'rm' ? rmName : nextAction.owner === 'system' ? 'System / RM' : '\u2014'}</span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              <Badge color={kycStatusColor(kycStatus)}>{kycStatus}</Badge>
            </div>
          </div>
        </Card>
      )}

      {/* KYC Workflow Action Panel — embedded from KYCModule */}
      {kycCustomer && kycCase && (
        <div style={{ marginBottom: 16 }} id="kyc-workflow-panel">
          {kycCase.status === KYCStatus.DATA_COLLECTION && (
            <DataCollectionPanel customer={kycCustomer} kyc={kyc} kycCase={kycCase} refresh={refreshC360} />
          )}
          {kycCase.status === KYCStatus.SCREENING && (
            <ScreeningPanel customer={kycCustomer} kyc={kyc} kycCase={kycCase} refresh={refreshC360} />
          )}
          {kycCase.status === KYCStatus.RM_REVIEW && runQualityGate && extractActivityProfile && (
            <RMReviewPanel customer={kycCustomer} kyc={kyc} kycCase={kycCase} monitoring={monitoring}
              runQualityGate={runQualityGate} extractActivityProfile={extractActivityProfile}
              refresh={refreshC360} draft={assessmentDraft} setDraft={setAssessmentDraft} isPeriodicReview={false} />
          )}
          {kycCase.status === KYCStatus.APPROVED && (
            <ApprovedPanel customer={kycCustomer} kyc={kyc} kycCase={kycCase} refresh={refreshC360} />
          )}
          {kycCase.status === KYCStatus.REVIEW_DUE && (
            <ReviewDuePanel customer={kycCustomer} kyc={kyc} kycCase={kycCase} refresh={refreshC360} />
          )}
          {kycCase.status === KYCStatus.UNDER_REVIEW && runQualityGate && extractActivityProfile && (
            <RMReviewPanel customer={kycCustomer} kyc={kyc} kycCase={kycCase} monitoring={monitoring}
              runQualityGate={runQualityGate} extractActivityProfile={extractActivityProfile}
              refresh={refreshC360} draft={assessmentDraft} setDraft={setAssessmentDraft} isPeriodicReview={true} />
          )}
          {kycCase.status === KYCStatus.REJECTED && (
            <RejectedPanel customer={kycCustomer} kycCase={kycCase} kyc={kyc} refresh={refreshC360} />
          )}
        </div>
      )}

      {/* Header card with relationship score */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <ST>{c.legalName}</ST>
          <Badge color={riskColor(data.riskRating)}>{data.riskRating || 'UNRATED'}</Badge>
          <Badge color={kycStatusColor(kycStatus)}>{kycStatus}</Badge>
          {insights?.relationshipScore && (
            <RelationshipScoreGauge score={insights.relationshipScore.score} />
          )}
        </div>
        {/* KYC one-liner */}
        {insights?.kycSummary && (
          <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 8, padding: '4px 10px', background: T.surfaceAlt, borderRadius: 4 }}>
            {insights.kycSummary.status} | {insights.kycSummary.riskRating || 'UNRATED'} risk
            {insights.kycSummary.daysUntilReview !== null && <> | Review in {insights.kycSummary.daysUntilReview > 0 ? `${insights.kycSummary.daysUntilReview} days` : <span style={{ color: T.red, fontWeight: 600 }}>OVERDUE</span>}</>}
            {' | '}{insights.kycSummary.openAlerts} open alert{insights.kycSummary.openAlerts !== 1 ? 's' : ''}
          </div>
        )}
        <div style={{ display: 'flex', gap: 24, fontSize: 12, color: T.textSecondary, flexWrap: 'wrap' }}>
          {c.registrationNumber && <span>Reg: <span style={{ color: T.text, fontWeight: 500 }}>{c.registrationNumber}</span></span>}
          <span>Type: <span style={{ color: T.text, fontWeight: 500 }}>{c.type}</span></span>
          {c.registeredAddress && <span>Address: <span style={{ color: T.text }}>{c.registeredAddress}</span></span>}
          {data.nextReviewDate && <span>Next review: <span style={{ color: T.text }}>{fmtDate(data.nextReviewDate)}</span></span>}
        </div>
      </Card>

      {/* Insights Row */}
      {insights && <InsightsRow insights={insights} />}

      {/* Suggestions Panel */}
      {insights?.suggestions?.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setSuggestionsOpen(o => !o)}>
            <ST>Proactive Suggestions ({insights.suggestions.length})</ST>
            <span style={{ fontSize: 12, color: T.textMuted }}>{suggestionsOpen ? '\u25B2' : '\u25BC'}</span>
          </div>
          {suggestionsOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {insights.suggestions.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: `1px solid ${T.borderLight}`, fontSize: 12 }}>
                  <Badge color={s.type === 'OPPORTUNITY' ? 'green' : s.type === 'RISK' ? 'red' : 'blue'}>{s.type}</Badge>
                  <span style={{ color: T.text }}>{s.message}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Summary stats with totals */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Total Balance" value={fmtCcy(totalBalance)} color={T.blue} />
        <StatCard label="Accounts" value={(data.accounts || []).length} />
        <StatCard label="Active Facilities" value={activeFacilities.length} />
        <StatCard label="Total Exposure" value={fmtCcy(totalExposure)} color={activeFacilities.length > 0 ? T.amber : T.textMuted} />
        <StatCard label="Open Alerts" value={(data.openAlerts || []).length} color={(data.openAlerts || []).length > 0 ? T.red : T.textMuted} />
        <StatCard label="Open SARs" value={(data.openSARs || []).length} color={(data.openSARs || []).length > 0 ? T.amber : T.textMuted} />
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        {/* Accounts */}
        <Card style={{ flex: 1 }}>
          <ST>Accounts</ST>
          {(data.accounts || []).length === 0 ? (
            <div style={{ color: T.textMuted, fontSize: 12, padding: '8px 0' }}>No accounts yet</div>
          ) : (
            data.accounts.map(acc => (
              <div key={acc.id} style={{ padding: '6px 0', borderBottom: `1px solid ${T.border}`, fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 500 }}>{acc.name || acc.accountNumber}</span>
                  <span style={{ fontWeight: 600, color: T.blue }}>{fmtCcy(acc.balance ?? acc.posted ?? acc.available ?? 0)}</span>
                </div>
                <div style={{ color: T.textSecondary, fontSize: 11 }}>{acc.accountNumber} — {acc.currency} — {acc.status}</div>
              </div>
            ))
          )}
        </Card>

        {/* Customer Details */}
        <div id="customer-details" style={{ flex: 1 }}><Card>
          <ST>Customer Details</ST>
          <KVLine label="Type" value={c.type} />
          <KVLine label="Registration" value={c.registrationNumber || '\u2014'} />
          <KVLine label="Address" value={c.registeredAddress || '\u2014'} />
          <KVLine label="SIC Codes" value={c.sicCodes?.join(', ') || '\u2014'} />
          <KVLine label="Created" value={fmtDate(c.createdAt)} />
        </Card></div>
      </div>

      {/* Facilities */}
      <Card style={{ marginBottom: 16 }}>
        <ST>Lending Facilities</ST>
        {(data.facilities || []).length === 0 ? (
          <div style={{ color: T.textMuted, fontSize: 12, padding: '8px 0' }}>No lending facilities</div>
        ) : (
          <DataTable
            headers={['Type', 'Amount', 'Currency', 'Status', 'Maturity']}
            rows={data.facilities.map(f => [
              f.type,
              fmtCcy(f.committedAmount || f.committed_amount || 0),
              f.currency,
              <Badge color={f.status === 'ACTIVE' ? 'green' : f.status === 'DRAFT' ? 'gray' : 'amber'}>{f.status}</Badge>,
              f.maturityDate ? fmtDate(f.maturityDate) : '\u2014',
            ])}
            compact
          />
        )}
      </Card>

      {/* Recent Transactions */}
      <Card style={{ marginBottom: 16 }}>
        <ST>Recent Transactions</ST>
        {(data.recentTransactions || []).length === 0 ? (
          <div style={{ color: T.textMuted, fontSize: 12, padding: '8px 0' }}>No recent transactions</div>
        ) : (
          <DataTable
            headers={['Reference', 'Type', 'Amount', 'Date']}
            rows={data.recentTransactions.slice(0, 10).map(txn => [
              <span style={{ fontFamily: mono, fontSize: 11 }}>{txn.reference || txn.id?.slice(0, 14)}</span>,
              txn.type || '\u2014',
              txn.legs ? fmtCcy(txn.legs[0]?.amount || 0) : '\u2014',
              fmtDate(txn.createdAt || txn.timestamp),
            ])}
            compact
          />
        )}
      </Card>

      {/* Open Alerts */}
      <div id="open-alerts"><Card style={{ marginBottom: 16 }}>
        <ST>Open Alerts</ST>
        {(data.openAlerts || []).length === 0 ? (
          <div style={{ color: T.textMuted, fontSize: 12, padding: '8px 0' }}>No open alerts</div>
        ) : (
          <DataTable
            headers={['Type', 'Severity', 'Status', 'Created']}
            rows={data.openAlerts.map(a => [
              a.type,
              <Badge color={a.severity === 'HIGH' || a.severity === 'CRITICAL' ? 'red' : 'amber'}>{a.severity}</Badge>,
              <Badge color="red">{a.status}</Badge>,
              fmtDate(a.createdAt),
            ])}
            compact
          />
        )}
      </Card></div>

      {/* Open SARs */}
      {(data.openSARs || []).length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <ST>Open SARs ({data.openSARs.length})</ST>
          <DataTable
            headers={['SAR ID', 'Status', 'Reason', 'Created']}
            rows={data.openSARs.map(s => [
              <span style={{ fontFamily: mono, fontSize: 11 }}>{s.id.slice(0, 16)}</span>,
              <Badge color={s.status === 'DRAFT' ? 'gray' : 'amber'}>{s.status}</Badge>,
              <span style={{ fontSize: 11 }}>{s.reason?.slice(0, 40)}</span>,
              fmtDate(s.createdAt),
            ])}
            compact
          />
        </Card>
      )}

      {/* FSCS Eligibility */}
      <Card style={{ marginBottom: 16 }}>
        <ST>FSCS Eligibility</ST>
        <FSCSTab customerId={custId} currentUser={currentUser} />
      </Card>

      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        {/* Contact History */}
        <Card style={{ flex: 1 }}>
          <ST>Recent Contacts</ST>
          {recentContacts.length === 0 ? (
            <div style={{ color: T.textMuted, fontSize: 12, padding: '8px 0' }}>No contacts recorded</div>
          ) : (
            recentContacts.map(ct => (
              <div key={ct.id} style={{ padding: '6px 0', borderBottom: `1px solid ${T.border}`, fontSize: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Badge color="blue">{ct.type}</Badge>
                  <span style={{ fontWeight: 500 }}>{ct.subject}</span>
                  <span style={{ marginLeft: 'auto', color: T.textMuted, fontSize: 11 }}>{fmtDate(ct.date)}</span>
                </div>
                {ct.notes && <div style={{ color: T.textSecondary, fontSize: 11, marginTop: 2 }}>{ct.notes}</div>}
              </div>
            ))
          )}
        </Card>

        {/* Open Tasks */}
        <Card style={{ flex: 1 }}>
          <ST>Open Tasks</ST>
          {recentTasks.length === 0 ? (
            <div style={{ color: T.textMuted, fontSize: 12, padding: '8px 0' }}>No open tasks</div>
          ) : (
            recentTasks.map(task => (
              <div key={task.id} style={{ padding: '6px 0', borderBottom: `1px solid ${T.border}`, fontSize: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Badge color={priorityColor(task.priority)}>{task.priority}</Badge>
                  <span style={{ fontWeight: 500 }}>{task.title}</span>
                </div>
                {task.dueDate && <div style={{ color: T.textMuted, fontSize: 11 }}>Due: {fmtDate(task.dueDate)}</div>}
              </div>
            ))
          )}
        </Card>
      </div>

      {/* Relationship Timeline */}
      {insights && <RelationshipTimeline data={data} insights={insights} />}
    </div>
  );
}

// ============================================================
// INSIGHTS HELPER COMPONENTS
// ============================================================

function RelationshipScoreGauge({ score }) {
  const color = score >= 70 ? T.green : score >= 40 ? T.amber : T.red;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
      <div style={{ position: 'relative', width: 36, height: 36 }}>
        <svg width="36" height="36" viewBox="0 0 36 36">
          <circle cx="18" cy="18" r="15" fill="none" stroke={T.borderLight} strokeWidth="3" />
          <circle cx="18" cy="18" r="15" fill="none" stroke={color} strokeWidth="3"
            strokeDasharray={`${(score / 100) * 94.2} 94.2`}
            strokeLinecap="round" transform="rotate(-90 18 18)" />
        </svg>
        <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 10, fontWeight: 700, color }}>{score}</span>
      </div>
      <span style={{ fontSize: 10, color: T.textMuted, fontWeight: 500 }}>Relationship<br />Score</span>
    </div>
  );
}

function InsightsRow({ insights }) {
  const { activityTrend, balanceTrend, facilityUtilisation, paymentTiming } = insights;

  const trendArrow = (trend) => trend === 'GROWING' || trend === 'INCREASING' ? '\u2191' : trend === 'DECLINING' || trend === 'SLOWING' ? '\u2193' : '\u2192';
  const trendColor = (trend) => trend === 'GROWING' || trend === 'INCREASING' || trend === 'IMPROVING' ? T.green : trend === 'DECLINING' || trend === 'SLOWING' ? T.red : T.textMuted;

  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
      {/* Activity Trend */}
      <Card style={{ flex: 1, minWidth: 200, padding: 14 }}>
        <div style={{ fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>Activity Trend</div>
        {activityTrend.trend === 'INSUFFICIENT_DATA' ? (
          <div style={{ fontSize: 12, color: T.textMuted }}>Insufficient data</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: trendColor(activityTrend.trend) }}>{trendArrow(activityTrend.trend)}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: trendColor(activityTrend.trend) }}>
                {activityTrend.changeCount > 0 ? '+' : ''}{activityTrend.changeCount}%
              </span>
            </div>
            <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 4 }}>
              Payment volume vs prior quarter
            </div>
            {activityTrend.daysSinceLastPayment > 30 && (
              <div style={{ fontSize: 11, color: T.red, fontWeight: 500, marginTop: 4 }}>
                {activityTrend.daysSinceLastPayment} days since last payment
              </div>
            )}
          </>
        )}
      </Card>

      {/* Balance Trend */}
      <Card style={{ flex: 1, minWidth: 200, padding: 14 }}>
        <div style={{ fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>Balance Trend</div>
        {balanceTrend.trend === 'INSUFFICIENT_DATA' ? (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.blue }}>{fmtCcy(balanceTrend.currentBalance)}</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Current balance (no trend data)</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: T.blue }}>{fmtCcy(balanceTrend.currentBalance)}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: trendColor(balanceTrend.trend) }}>
                {trendArrow(balanceTrend.trend)} {balanceTrend.growthPct > 0 ? '+' : ''}{balanceTrend.growthPct}%
              </span>
            </div>
            {balanceTrend.monthlyAverages.length > 0 && (
              <MiniSparkline data={balanceTrend.monthlyAverages.map(m => m.avgBalance)} color={trendColor(balanceTrend.trend)} />
            )}
            <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>6-month avg: {fmtCcy(balanceTrend.sixMonthAvg)}</div>
          </>
        )}
      </Card>

      {/* Facility Utilisation */}
      <Card style={{ flex: 1, minWidth: 200, padding: 14 }}>
        <div style={{ fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>Facility Utilisation</div>
        {facilityUtilisation.length === 0 ? (
          <div style={{ fontSize: 12, color: T.textMuted }}>No active facilities</div>
        ) : (
          facilityUtilisation.slice(0, 3).map(f => (
            <div key={f.facilityId} style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                <span style={{ color: T.textSecondary }}>{f.type}</span>
                <span style={{ fontWeight: 600, color: f.status === 'HIGH' ? T.red : f.status === 'LOW' ? T.amber : T.green }}>{f.utilisation}%</span>
              </div>
              <div style={{ height: 4, background: T.borderLight, borderRadius: 2 }}>
                <div style={{ height: 4, borderRadius: 2, width: `${Math.min(100, f.utilisation)}%`, background: f.status === 'HIGH' ? T.red : f.status === 'LOW' ? T.amber : T.green }} />
              </div>
            </div>
          ))
        )}
      </Card>

      {/* Payment Timing */}
      <Card style={{ flex: 1, minWidth: 200, padding: 14 }}>
        <div style={{ fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>Payment Timing</div>
        {paymentTiming.recentTrend === 'INSUFFICIENT_DATA' ? (
          <div style={{ fontSize: 12, color: T.textMuted }}>Insufficient data</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: trendColor(paymentTiming.recentTrend === 'SLOWING' ? 'DECLINING' : paymentTiming.recentTrend) }}>
                {trendArrow(paymentTiming.recentTrend === 'SLOWING' ? 'DECLINING' : paymentTiming.recentTrend)}
              </span>
              {paymentTiming.avgPaymentFrequencyDays && (
                <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{paymentTiming.avgPaymentFrequencyDays}d avg</span>
              )}
            </div>
            <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 4 }}>
              Between outbound payments
            </div>
            {paymentTiming.avgPaymentAmount > 0 && (
              <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>
                Avg amount: {fmtCcy(paymentTiming.avgPaymentAmount)}
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function MiniSparkline({ data, color }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 140, h = 24;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ');
  return (
    <svg width={w} height={h} style={{ marginTop: 4 }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RelationshipTimeline({ data, insights }) {
  const events = [];

  // Account opened
  const customer = data.customer;
  if (customer?.createdAt) {
    events.push({ date: customer.createdAt, label: 'Customer onboarded', type: 'milestone' });
  }

  // Accounts opened
  for (const acc of (data.accounts || []).slice(0, 3)) {
    if (acc.openedAt) events.push({ date: acc.openedAt, label: `${acc.name || 'Account'} opened`, type: 'account' });
  }

  // Facilities
  for (const f of (data.facilities || []).slice(0, 3)) {
    if (f.createdAt) events.push({ date: f.createdAt, label: `${f.type} facility granted`, type: 'facility' });
  }

  // Alerts
  for (const a of (data.openAlerts || []).slice(0, 2)) {
    if (a.createdAt) events.push({ date: a.createdAt, label: `Alert: ${a.type}`, type: 'alert' });
  }
  for (const a of (data.recentClosedAlerts || []).slice(0, 2)) {
    if (a.createdAt) events.push({ date: a.createdAt, label: `Alert resolved: ${a.type}`, type: 'resolved' });
  }

  if (events.length === 0) return null;

  // Sort chronologically
  events.sort((a, b) => new Date(a.date) - new Date(b.date));

  const typeColors = { milestone: T.blue, account: T.green, facility: T.purple, alert: T.red, resolved: T.green };

  return (
    <Card style={{ marginBottom: 16 }}>
      <ST>Relationship Timeline</ST>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0, overflowX: 'auto', paddingBottom: 8 }}>
        {events.map((ev, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100, flex: 1 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: typeColors[ev.type] || T.blue, flexShrink: 0 }} />
            {i < events.length - 1 && <div style={{ width: '100%', height: 2, background: T.borderLight, marginTop: -6 }} />}
            <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4, textAlign: 'center' }}>{fmtDate(ev.date)}</div>
            <div style={{ fontSize: 10, color: T.textSecondary, textAlign: 'center', fontWeight: 500, lineHeight: 1.3, marginTop: 2 }}>{ev.label}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function KVLine({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
      <span style={{ color: T.textSecondary }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}


// ============================================================
// MY TASKS — Issue 3: claim switches to open, Issue 4: clickable rows
// ============================================================

function TasksView({ crm, kyc, rmId, openCustomer, refresh, showToast, showError }) {
  const [filter, setFilter] = useState('open');
  const [expandedTask, setExpandedTask] = useState(null);

  const allTasks = Array.from(crm.tasks.values());
  const myTasks = allTasks.filter(t => t.assignedTo === rmId);
  const openTasks = myTasks.filter(t => t.status === TaskStatus.OPEN);
  const overdueTasks = crm.getOverdueTasks().filter(t => t.assignedTo === rmId);
  const completedTasks = myTasks.filter(t => t.status === TaskStatus.COMPLETED);
  const unassigned = allTasks.filter(t => !t.assignedTo && t.status === TaskStatus.OPEN);

  let displayed;
  if (filter === 'open') displayed = openTasks;
  else if (filter === 'overdue') displayed = overdueTasks;
  else if (filter === 'completed') displayed = completedTasks;
  else if (filter === 'unassigned') displayed = unassigned;
  else displayed = openTasks;

  const handleComplete = async (taskId) => {
    try {
      await crm.completeTask(taskId, rmId, 'Completed from dashboard');
      showToast('Task completed');
      setExpandedTask(null);
      await refresh();
    } catch (e) { showError(e.message); }
  };

  // Issue 3: after claim, switch to 'open' so user sees the task in their list
  const handleClaim = async (taskId) => {
    try {
      await crm.reassignTask(taskId, rmId);
      showToast('Task assigned to you');
      await refresh();
      setFilter('open');
    } catch (e) { showError(e.message); }
  };

  const toggleExpand = (taskId) => {
    setExpandedTask(expandedTask === taskId ? null : taskId);
  };

  return (
    <div>
      <ST>My Tasks</ST>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Open" value={openTasks.length} color={T.amber} />
        <StatCard label="Overdue" value={overdueTasks.length} color={T.red} />
        <StatCard label="Completed" value={completedTasks.length} color={T.green} />
        <StatCard label="Unassigned" value={unassigned.length} color={T.textMuted} />
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {[
          { key: 'open', label: `Open (${openTasks.length})` },
          { key: 'overdue', label: `Overdue (${overdueTasks.length})` },
          { key: 'completed', label: `Completed (${completedTasks.length})` },
          { key: 'unassigned', label: `Unassigned (${unassigned.length})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '4px 10px', fontSize: 11, border: `1px solid ${filter === f.key ? T.blue : T.border}`,
            background: filter === f.key ? T.blueBg : 'transparent', color: filter === f.key ? T.blue : T.textSecondary,
            borderRadius: 4, cursor: 'pointer', fontFamily: font,
          }}>{f.label}</button>
        ))}
      </div>

      {displayed.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>No tasks in this view</div>
      ) : (
        <div>
          {/* Issue 4: clickable task rows with inline detail */}
          {displayed.map(task => {
            const cust = task.customerId ? kyc?.getCustomer(task.customerId) : null;
            const isExpanded = expandedTask === task.id;

            return (
              <div key={task.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                {/* Task row */}
                <div
                  onClick={() => toggleExpand(task.id)}
                  style={{
                    display: 'grid', gridTemplateColumns: '70px 1fr 150px 100px 90px 120px',
                    gap: 8, alignItems: 'center', padding: '8px 10px', fontSize: 12,
                    cursor: 'pointer', background: isExpanded ? T.blueBg : 'transparent',
                  }}
                  onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = T.surfaceAlt; }}
                  onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div><Badge color={priorityColor(task.priority)}>{task.priority}</Badge></div>
                  <div style={{ fontWeight: 500 }}>{task.title}</div>
                  <div style={{ color: T.textSecondary }}>{cust?.legalName || '\u2014'}</div>
                  <div style={{ fontSize: 11, color: T.textMuted }}>{task.type}</div>
                  <div>{task.dueDate ? <span style={{ color: task.dueDate < new Date().toISOString() ? T.red : T.textSecondary, fontSize: 11 }}>{fmtDate(task.dueDate)}</span> : '\u2014'}</div>
                  <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 4 }}>
                    {task.status === TaskStatus.OPEN && <Btn onClick={() => handleComplete(task.id)} small>Complete</Btn>}
                    {filter === 'unassigned' && <Btn onClick={() => handleClaim(task.id)} small variant="secondary">Claim</Btn>}
                  </div>
                </div>

                {/* Issue 4: Inline task detail panel */}
                {isExpanded && (
                  <div style={{ padding: '12px 16px 16px 80px', background: T.blueBg, borderTop: `1px solid ${T.blueBorder}` }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12, marginBottom: 12 }}>
                      <div>
                        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 2 }}>Type</div>
                        <div>{task.type?.replace(/_/g, ' ')}</div>
                      </div>
                      <div>
                        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 2 }}>Assigned To</div>
                        <div>{task.assignedTo || <span style={{ color: T.textMuted }}>Unassigned</span>}</div>
                      </div>
                      <div>
                        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 2 }}>Due Date</div>
                        <div>{task.dueDate ? fmtDate(task.dueDate) : '\u2014'}</div>
                      </div>
                      <div>
                        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 2 }}>Customer</div>
                        {cust ? (
                          <span style={{ color: T.blue, cursor: 'pointer' }} onClick={() => openCustomer(task.customerId, 'tasks')}>{cust.legalName} &rarr;</span>
                        ) : <span style={{ color: T.textMuted }}>{'\u2014'}</span>}
                      </div>
                    </div>
                    {task.description && (
                      <div style={{ fontSize: 12, marginBottom: 12 }}>
                        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 2 }}>Description</div>
                        <div style={{ color: T.textSecondary }}>{task.description}</div>
                      </div>
                    )}
                    {task.completedNotes && (
                      <div style={{ fontSize: 12, marginBottom: 12 }}>
                        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 2 }}>Completion Notes</div>
                        <div style={{ color: T.textSecondary }}>{task.completedNotes}</div>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      {task.status === TaskStatus.OPEN && <Btn onClick={() => handleComplete(task.id)} small>Complete Task</Btn>}
                      {!task.assignedTo && <Btn onClick={() => handleClaim(task.id)} small variant="secondary">Claim Task</Btn>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============================================================
// PIPELINES — Issue 1: async load + field name handling
// ============================================================

function PipelinesView({ crm, openCustomer }) {
  const [onboarding, setOnboarding] = useState(crm.getOnboardingPipeline());
  const [lending, setLending] = useState(crm.getLendingPipeline());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (crm.loadPipelines) {
      setLoading(true);
      crm.loadPipelines()
        .then(data => {
          if (data) {
            setOnboarding(data.onboarding || {});
            setLending(data.lending || {});
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [crm]);

  const onboardingStages = ['DATA_COLLECTION', 'SCREENING', 'RM_REVIEW', 'APPROVED'];
  const lendingStages = ['DRAFT', 'APPROVED', 'ACTIVE'];

  if (loading && Object.keys(onboarding).length === 0) {
    return <div><ST>Pipelines</ST><Card><div style={{ color: T.textMuted, padding: 20, textAlign: 'center' }}>Loading pipelines...</div></Card></div>;
  }

  return (
    <div>
      <ST>Onboarding Pipeline</ST>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, overflowX: 'auto' }}>
        {onboardingStages.map(stage => (
          <div key={stage} style={{ flex: 1, minWidth: 180 }}>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary }}>{stage.replace(/_/g, ' ')}</span>
                <span style={{ fontSize: 11, background: T.surface, padding: '1px 6px', borderRadius: 8, color: T.textMuted }}>{onboarding[stage]?.length || 0}</span>
              </div>
              {(onboarding[stage] || []).map(entry => (
                <div key={entry.customerId} style={{
                  padding: '6px 8px', marginBottom: 4, borderRadius: 4,
                  background: T.bg, border: `1px solid ${T.border}`, fontSize: 12,
                  cursor: 'pointer',
                }} onClick={() => openCustomer(entry.customerId, 'pipelines')}>
                  <div style={{ fontWeight: 500, color: T.blue }}>{entry.legalName || entry.legal_name || 'Unknown'}</div>
                  {entry.riskRating && <Badge color={riskColor(entry.riskRating)}>{entry.riskRating}</Badge>}
                </div>
              ))}
            </Card>
          </div>
        ))}
      </div>

      <ST>Lending Pipeline</ST>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, overflowX: 'auto' }}>
        {lendingStages.map(stage => (
          <div key={stage} style={{ flex: 1, minWidth: 180 }}>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary }}>{stage}</span>
                <span style={{ fontSize: 11, background: T.surface, padding: '1px 6px', borderRadius: 8, color: T.textMuted }}>{lending[stage]?.length || 0}</span>
              </div>
              {(lending[stage] || []).map(entry => (
                <div key={entry.facilityId} style={{
                  padding: '6px 8px', marginBottom: 4, borderRadius: 4,
                  background: T.bg, border: `1px solid ${T.border}`, fontSize: 12,
                  cursor: 'pointer',
                }} onClick={() => openCustomer(entry.customerId, 'pipelines')}>
                  <div style={{ fontWeight: 500, color: T.blue }}>{entry.customerName || entry.customer_name || 'Unknown'}</div>
                  <div style={{ fontSize: 11, color: T.textSecondary }}>{entry.type} — {fmtCcy(entry.committedAmount || entry.committed_amount || 0)} {entry.currency}</div>
                </div>
              ))}
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
}


// ============================================================
// COMPLAINTS
// ============================================================

const complaintStatusColor = s => ({
  RECEIVED: 'amber', ACKNOWLEDGED: 'blue', INVESTIGATING: 'blue',
  RESOLVED: 'green', ESCALATED_TO_FOS: 'red',
}[s] || 'gray');

const COMPLAINT_STAGES = ['RECEIVED', 'ACKNOWLEDGED', 'INVESTIGATING', 'RESOLVED'];
const FCA_GUIDANCE = {
  RECEIVED: 'DISP 1.3: Acknowledge within 5 business days. Record complaint details, inform customer of process, and assign handler.',
  ACKNOWLEDGED: 'DISP 1.4: Begin investigation promptly. Gather evidence, review correspondence, and assess whether complaint has merit.',
  INVESTIGATING: 'DISP 1.6: Issue final response within 8 weeks of receipt. If unable to resolve, inform customer of right to refer to FOS.',
  RESOLVED: 'DISP 1.5: Maintain records for 3 years. If upheld, ensure redress is fair and appropriate.',
  ESCALATED_TO_FOS: 'DISP 2: Complaint referred to Financial Ombudsman Service. Cooperate fully with FOS investigation.',
};

function ComplaintProgress({ status }) {
  const idx = COMPLAINT_STAGES.indexOf(status);
  const activeIdx = status === 'ESCALATED_TO_FOS' ? -1 : idx;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, margin: '12px 0' }}>
      {COMPLAINT_STAGES.map((stage, i) => {
        const done = activeIdx >= 0 && i <= activeIdx;
        const current = i === activeIdx;
        return (
          <div key={stage} style={{ display: 'flex', alignItems: 'center', flex: i < COMPLAINT_STAGES.length - 1 ? 1 : 'none' }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 600, fontFamily: font, flexShrink: 0,
              background: done ? (current ? T.blue : T.green) : T.surfaceAlt,
              color: done ? '#fff' : T.textMuted,
              border: current ? `2px solid ${T.blue}` : 'none',
            }}>{i + 1}</div>
            <div style={{ fontSize: 9, position: 'absolute', marginTop: 38, width: 60, textAlign: 'center', marginLeft: -16, color: done ? T.text : T.textMuted }}>{stage.replace(/_/g, ' ')}</div>
            {i < COMPLAINT_STAGES.length - 1 && (
              <div style={{ flex: 1, height: 2, background: activeIdx > i ? T.green : T.border, margin: '0 4px' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ComplaintsView({ crm, kyc, openCustomer, refresh, showToast, showError, initialEntityId }) {
  const [filter, setFilter] = useState(initialEntityId ? 'all' : 'open');
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(initialEntityId || null);
  const [resolutionNotes, setResolutionNotes] = useState({});
  const [formCustomerId, setFormCustomerId] = useState('');
  const [formSubject, setFormSubject] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formChannel, setFormChannel] = useState('PHONE');

  const customers = kyc?.getAllCustomers() || [];
  const allComplaints = Array.from(crm.complaints.values());
  const openComplaints = crm.getOpenComplaints();
  const overdueComplaints = crm.getOverdueComplaints();
  const resolvedComplaints = allComplaints.filter(c => c.status === ComplaintStatus.RESOLVED);
  const escalated = allComplaints.filter(c => c.status === ComplaintStatus.ESCALATED_TO_FOS);

  let displayed;
  if (filter === 'open') displayed = openComplaints;
  else if (filter === 'overdue') displayed = overdueComplaints;
  else if (filter === 'resolved') displayed = resolvedComplaints;
  else if (filter === 'escalated') displayed = escalated;
  else displayed = allComplaints;

  const daysElapsed = (receivedDate) => {
    const d = Math.floor((Date.now() - new Date(receivedDate).getTime()) / 86400000);
    return d;
  };
  const daysColor = (d) => d > 40 ? T.red : d > 5 ? T.amber : T.textSecondary;

  const handleCreate = async () => {
    try {
      await crm.createComplaint(formCustomerId, { subject: formSubject, description: formDescription, channel: formChannel });
      showToast('Complaint created');
      setShowForm(false);
      setFormSubject('');
      setFormDescription('');
      await refresh();
    } catch (e) { showError(e.message); }
  };

  const handleAcknowledge = async (id) => {
    try {
      await crm.acknowledgeComplaint(id, 'rm_sarah_j');
      showToast('Complaint acknowledged');
      await refresh();
    } catch (e) { showError(e.message); }
  };

  const handleResolve = async (id) => {
    const notes = resolutionNotes[id];
    if (!notes || !notes.trim()) { showError('Resolution notes are required before resolving'); return; }
    try {
      await crm.resolveComplaint(id, { resolution: notes.trim(), resolvedBy: 'rm_sarah_j', upheld: false });
      showToast('Complaint resolved');
      setExpandedId(null);
      await refresh();
    } catch (e) { showError(e.message); }
  };

  const handleEscalate = async (id) => {
    try {
      await crm.escalateToFOS(id, { reason: 'Customer dissatisfied with resolution' });
      showToast('Escalated to FOS');
      setExpandedId(null);
      await refresh();
    } catch (e) { showError(e.message); }
  };

  const dashboard = crm.getComplaintsDashboard();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <ST>Complaints (FCA DISP)</ST>
        <Btn onClick={() => setShowForm(!showForm)} small>{showForm ? 'Cancel' : 'New Complaint'}</Btn>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Total" value={dashboard.total || 0} />
        <StatCard label="Open" value={dashboard.open || 0} color={T.amber} />
        <StatCard label="Overdue (8wk)" value={dashboard.overdue || 0} color={T.red} />
        <StatCard label="Avg Resolution" value={`${dashboard.avgResolutionDays || 0}d`} />
        <StatCard label="Upheld Rate" value={`${dashboard.upheldRate || 0}%`} />
        <StatCard label="Redress Total" value={fmtCcy(dashboard.redressTotal || 0)} />
      </div>

      {showForm && (
        <Card style={{ maxWidth: 600, marginBottom: 16 }}>
          <ST>Log New Complaint</ST>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <CustomerTypeahead customers={customers} value={formCustomerId} onSelect={setFormCustomerId} />
            <Sel label="Channel" value={formChannel} onChange={setFormChannel}
              options={Object.values(ComplaintChannel).map(ch => ({ value: ch, label: ch.replace('_', ' ') }))} />
            <Inp label="Subject" value={formSubject} onChange={e => setFormSubject(e.target.value)} placeholder="Brief description of complaint..." />
            <div>
              <label style={{ fontSize: 11, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Description</label>
              <textarea value={formDescription} onChange={e => setFormDescription(e.target.value)}
                placeholder="Full details of the complaint..."
                rows={3}
                style={{ width: '100%', padding: '6px 8px', fontSize: 12, background: T.bg, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, fontFamily: font, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <Btn onClick={handleCreate}>Submit Complaint</Btn>
          </div>
        </Card>
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {[
          { key: 'open', label: `Open (${openComplaints.length})` },
          { key: 'overdue', label: `Overdue (${overdueComplaints.length})` },
          { key: 'resolved', label: `Resolved (${resolvedComplaints.length})` },
          { key: 'escalated', label: `FOS (${escalated.length})` },
          { key: 'all', label: `All (${allComplaints.length})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '4px 10px', fontSize: 11, border: `1px solid ${filter === f.key ? T.blue : T.border}`,
            background: filter === f.key ? T.blueBg : 'transparent', color: filter === f.key ? T.blue : T.textSecondary,
            borderRadius: 4, cursor: 'pointer', fontFamily: font,
          }}>{f.label}</button>
        ))}
      </div>

      {displayed.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>No complaints in this view</div>
      ) : (
        <div>
          {/* Header row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 140px) 1fr 180px 80px 90px 60px', gap: 8, padding: '6px 10px', fontSize: 11, fontWeight: 600, color: T.textSecondary, borderBottom: `2px solid ${T.border}` }}>
            <div>Status</div><div>Customer</div><div>Subject</div><div>Channel</div><div>Received</div><div>Days</div>
          </div>
          {displayed.map(comp => {
            const cust = kyc?.getCustomer(comp.customerId);
            const isExpanded = expandedId === comp.id;
            const days = daysElapsed(comp.receivedDate);

            return (
              <div key={comp.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                <div
                  onClick={() => setExpandedId(isExpanded ? null : comp.id)}
                  style={{
                    display: 'grid', gridTemplateColumns: 'minmax(120px, 140px) 1fr 180px 80px 90px 60px',
                    gap: 8, alignItems: 'center', padding: '8px 10px', fontSize: 12,
                    cursor: 'pointer', background: isExpanded ? T.blueBg : 'transparent',
                  }}
                  onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = T.surfaceAlt; }}
                  onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ overflow: 'hidden' }}><Badge color={complaintStatusColor(comp.status)}><span style={{ whiteSpace: 'nowrap', fontSize: 10 }}>{comp.status.replace(/_/g, ' ')}</span></Badge></div>
                  <div>
                    {cust ? (
                      <span style={{ fontWeight: 500, color: T.blue, cursor: 'pointer' }} onClick={e => { e.stopPropagation(); openCustomer(comp.customerId, 'complaints'); }}>{cust.legalName}</span>
                    ) : <span style={{ color: T.textMuted }}>{comp.customerId}</span>}
                  </div>
                  <div style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{comp.subject}</div>
                  <div style={{ fontSize: 11, color: T.textMuted }}>{comp.channel}</div>
                  <div style={{ fontSize: 11 }}>{fmtDate(comp.receivedDate)}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: daysColor(days) }}>{days}d</div>
                </div>

                {isExpanded && (
                  <div style={{ padding: '12px 16px 16px 16px', background: T.blueBg, borderTop: `1px solid ${T.blueBorder}` }}>
                    {/* Workflow progress */}
                    <div style={{ position: 'relative', marginBottom: 32 }}>
                      <ComplaintProgress status={comp.status} />
                    </div>

                    {/* FCA DISP guidance */}
                    <div style={{ padding: '8px 12px', background: T.amberBg || '#fff8e1', border: `1px solid ${T.amber}30`, borderRadius: 4, marginBottom: 12, fontSize: 11, color: T.textSecondary }}>
                      <span style={{ fontWeight: 600, color: T.amber }}>FCA Guidance:</span> {FCA_GUIDANCE[comp.status] || FCA_GUIDANCE.RECEIVED}
                    </div>

                    {/* Detail grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, fontSize: 12, marginBottom: 12 }}>
                      <div>
                        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 2 }}>Subject</div>
                        <div style={{ fontWeight: 500 }}>{comp.subject}</div>
                      </div>
                      <div>
                        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 2 }}>Channel</div>
                        <div>{comp.channel?.replace(/_/g, ' ')}</div>
                      </div>
                      <div>
                        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 2 }}>Days Elapsed</div>
                        <div style={{ fontWeight: 600, color: daysColor(days) }}>{days} days {days > 40 ? '(OVERDUE)' : days > 5 ? '(review ack)' : ''}</div>
                      </div>
                      <div>
                        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 2 }}>Customer</div>
                        {cust ? (
                          <span style={{ color: T.blue, cursor: 'pointer' }} onClick={() => openCustomer(comp.customerId, 'complaints')}>{cust.legalName} &rarr;</span>
                        ) : <span>{comp.customerId}</span>}
                      </div>
                      <div>
                        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 2 }}>Received</div>
                        <div>{fmtDate(comp.receivedDate)}</div>
                      </div>
                      {comp.acknowledgedDate && (
                        <div>
                          <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 2 }}>Acknowledged</div>
                          <div>{fmtDate(comp.acknowledgedDate)}</div>
                        </div>
                      )}
                    </div>

                    {comp.description && (
                      <div style={{ fontSize: 12, marginBottom: 12 }}>
                        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 2 }}>Description</div>
                        <div style={{ color: T.textSecondary, whiteSpace: 'pre-wrap' }}>{comp.description}</div>
                      </div>
                    )}

                    {comp.resolution && (
                      <div style={{ fontSize: 12, marginBottom: 12 }}>
                        <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 2 }}>Resolution</div>
                        <div style={{ color: T.green }}>{comp.resolution}</div>
                      </div>
                    )}

                    {/* Resolution notes (required before resolving) */}
                    {comp.status === 'INVESTIGATING' && (
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: 11, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Resolution Notes (required)</label>
                        <textarea
                          value={resolutionNotes[comp.id] || ''}
                          onChange={e => setResolutionNotes(prev => ({ ...prev, [comp.id]: e.target.value }))}
                          placeholder="Describe the resolution, outcome, and any redress offered..."
                          rows={3}
                          style={{ width: '100%', padding: '6px 8px', fontSize: 12, background: T.bg, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, fontFamily: font, resize: 'vertical', boxSizing: 'border-box' }}
                        />
                      </div>
                    )}

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: 8 }}>
                      {comp.status === 'RECEIVED' && <Btn onClick={() => handleAcknowledge(comp.id)} small>Acknowledge</Btn>}
                      {comp.status === 'INVESTIGATING' && <Btn onClick={() => handleResolve(comp.id)} small>Resolve</Btn>}
                      {comp.status === 'INVESTIGATING' && <Btn onClick={() => handleEscalate(comp.id)} small variant="secondary">Escalate to FOS</Btn>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============================================================
// ADD CONTACT
// ============================================================

function AddContactView({ crm, kyc, rmId, refresh, showToast, showError }) {
  const customers = kyc?.getAllCustomers() || [];
  const [customerId, setCustomerId] = useState('');
  const [type, setType] = useState('CALL');
  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = async () => {
    try {
      await crm.addContactRecord(customerId, { type, subject, notes, rmId });
      showToast('Contact recorded');
      setSubject('');
      setNotes('');
      await refresh();
    } catch (e) { showError(e.message); }
  };

  return (
    <div>
      <ST>Record Contact</ST>
      <Card style={{ maxWidth: 600 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <CustomerTypeahead customers={customers} value={customerId} onSelect={setCustomerId} />
          <Sel label="Type" value={type} onChange={setType}
            options={Object.values(ContactType).map(t => ({ value: t, label: t }))} />
          <Inp label="Subject" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Meeting subject or call topic..." />
          <div>
            <label style={{ fontSize: 11, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Key discussion points, follow-up actions..."
              rows={4}
              style={{ width: '100%', padding: '6px 8px', fontSize: 12, background: T.bg, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, fontFamily: font, resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          <Btn onClick={handleSubmit}>Save Contact</Btn>
        </div>
      </Card>

      {customerId && (() => {
        const history = crm.getContactHistory(customerId);
        if (history.length === 0) return null;
        return (
          <Card style={{ marginTop: 16, maxWidth: 600 }}>
            <ST>Recent Contacts for this Customer</ST>
            {history.slice(0, 5).map(ct => (
              <div key={ct.id} style={{ padding: '6px 0', borderBottom: `1px solid ${T.border}`, fontSize: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Badge color="blue">{ct.type}</Badge>
                  <span style={{ fontWeight: 500 }}>{ct.subject}</span>
                  <span style={{ marginLeft: 'auto', color: T.textMuted, fontSize: 11 }}>{fmtDate(ct.date)}</span>
                </div>
              </div>
            ))}
          </Card>
        );
      })()}
    </div>
  );
}
